import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { ISelectOptionProps, IFileDetails } from 'components/form/types';
import Form from 'components/form/Form';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import { buildClientEntity } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { QueryClientProvider, useQueryClient } from 'react-query';
import { formatDate } from 'utils/helpers/formatters';
import FormGroup from 'components/form/FormGroup';
import UpdateEntity from 'components/crud/UpdateEntity';
import Col from 'react-bootstrap/Col';
import { useModal } from 'store/modal';
import Row from 'react-bootstrap/Row';
import { useHistory } from 'react-router';
import Attachments from '../../components/Attachments';
import { ILeave } from '../types';

interface IProps {
  title: string;
  dropdowns: {
    user: ISelectOptionProps[];
    types: ISelectOptionProps[];
    paidSubTypes: ISelectOptionProps[];
    additionalSubTypes: ISelectOptionProps[];
  };
  leave: ILeave;
}

export interface IАttachment {
  id: number;
  file_name: string;
  extension: string;
}

const ManuallyEdit: FC<IProps> = ({ title, dropdowns, leave }: IProps) => {
  const history = useHistory();
  const leaveTypes: { [key: string]: string } = {
    sick: 'sick',
    paid: 'paid',
    unpaid: 'unpaid',
    additional: 'additional',
  };
  const currentType = dropdowns.types.find((opt: any) => leave.type === opt.value);
  const methods = useForm({
    defaultValues: {
      type: currentType,
      sub_type:
        currentType?.value === leaveTypes.paid
          ? dropdowns.paidSubTypes.find((opt: any) => leave.sub_type === opt.value)
          : dropdowns.additionalSubTypes.find((opt: any) => leave.sub_type === opt.value),
      user_id: dropdowns.user.find((opt: any) => leave.user_id === opt.value),
      take_paid_days: leave.take_paid_days,
      date_from: new Date(leave.date_from),
      date_to: new Date(leave.date_to),
      request_document_id: leave.request_document_id,
      order_document_id: leave.order_document_id,
      comment: leave.comment,
    },
  });
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { watch } = methods;
  const [isFileUploading, setIsFileUploading] = useState(false);
  const { showModal } = useModal();
  const [startDate, setStartDate] = useState(new Date());
  const [uploadedFiles, setUploadedFiles] = useState<IFileDetails[]>([]);
  const [oldUploadedFiles, setoldUploadedFiles] = useState(leave.attachments);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState<string[]>([]);
  const [holidaysCache, setHolidaysCache] = useState<{ [year: number]: string[] }>({});
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    // Fetch the holidays for the selected year
    const fetchHolidays = async () => {
      // If holidays for the year are already fetched, skip fetching
      if (holidaysCache[selectedYear]) {
        return;
      }

      try {
        const response = await API.get(`holidays/${selectedYear}`);
        const holidayDates = response.data.items.map((item: any) => item.date);
        // Update the cache with the newly fetched holidays
        setHolidaysCache(prevCache => ({
          ...prevCache,
          [selectedYear]: holidayDates,
        }));
      } catch (error) {
        showModal({
          type: 'error',
          content: createErrorFromResponse(error).message,
        });
      }
    };

    fetchHolidays();
  }, [selectedYear, holidaysCache]);

  const onFilesUploaded = (fl: IFileDetails[]) => {
    setUploadedFiles([...uploadedFiles, ...fl]);
  };

  const onFilesDeleted = (fl: any) => {
    setUploadedFiles(current => current.filter(file => fl.name !== file.name));
  };

  const fieldType = watch('type');

  const isHoliday = (date: Date) => {
    const day = date.getDay();
    const formattedDate = formatDate(date, 'YYY-mm-dd');
    const holidaysForYear = holidaysCache[date.getFullYear()] || [];
    // Return true if it's not a weekend and not a holiday, false otherwise
    return !(day === 0 || day === 6 || holidaysForYear.includes(formattedDate));
  };

  const formatUserAction = (actionArray: { user_name: string; timestamp: string }[], actionType: string) => {
    return actionArray.map(action => (
      <tr key={`${actionType}-${action.timestamp}`}>
        <td>
          <b>{actionType}</b> {t('leaves.from')} {action.user_name} {t('leaves.on')}{' '}
          {formatDate(new Date(action.timestamp), 'dd.mm.Y hh:ii')}
          {t('leaves.hoursSmall')}
        </td>
      </tr>
    ));
  };

  const formatSubtractedDays = (subtractedDaysByYears: { [year: string]: number }) => {
    return Object.entries(subtractedDaysByYears).map(
      ([year, days], index) =>
        // if days == 0 then don't show this year
        days > 0 && (
          <tr key={`subtracted-${year}-${index}`}>
            <td>
              {t('leaves.fromYear')} {year} {t('leaves.yearShortOnlyBG')} {t('leaves.used')} <b>{days}</b>{' '}
              {t('leaves.days')}
            </td>
          </tr>
        )
    );
  };

  // @ts-ignore
  const fieldTypeValue = fieldType?.value;

  const currentSubTypes = fieldTypeValue === leaveTypes.paid ? dropdowns.paidSubTypes : dropdowns.additionalSubTypes;
  const showSubType = fieldTypeValue === leaveTypes.paid || fieldTypeValue === leaveTypes.additional;
  const isManuallyCreatedLeave = leave.is_manually_added;

  return (
    <Row className="d-flex flex-column flex-lg-row justify-content-between">
      <Col className="col-lg-6">
        <>
          <QueryClientProvider client={queryClient}>
            <UpdateEntity
              id={id}
              entity="leaves/manually"
              methods={methods}
              queryKey={buildClientEntity('employer-leaves-edit', client)}
              onSuccess={(res: any) => {
                history.push('/leaves/completed');
                showModal({
                  type: 'success',
                  content: <>{t('leaves.manuallyEditedLeave')}</>,
                });
              }}
            >
              {(edit: any) => (
                <Form
                  title={title}
                  methods={methods}
                  btnLabel={t('leaves.manuallyEditLeaveCreate')}
                  primaryActionBtnType="primary"
                  isUploading={isFileUploading}
                  customError
                  onSubmit={(data: any) => {
                    showModal({
                      type: 'confirm',
                      heading: t('common.confirm'),
                      content: t('leaves.manuallyEditLeaveCreateConfirmation'),
                      onConfirm: async () => {
                        try {
                          const totalAttachments = uploadedFiles
                            .map(file => file.id)
                            .concat(oldUploadedFiles.map((file: IАttachment) => file.id));
                          const formatData = {
                            user_id: data?.user_id.value,
                            type: data?.type.value,
                            date_from: formatDate(data.date_from, 'YYY-mm-dd'),
                            date_to: formatDate(data.date_to, 'YYY-mm-dd'),
                            attachments: totalAttachments,
                            take_paid_days: data.take_paid_days && fieldTypeValue === leaveTypes.paid ? 1 : 0,
                            request_document_id: data?.request_document_id,
                            order_document_id: data?.order_document_id ?? null,
                            comment: data.comment,
                            ...(leave.part_of_day ? { part_of_day: leave.part_of_day } : {}),
                            ...(data.type.value === 'paid' || data.type.value === 'additional'
                              ? {
                                  sub_type: data?.sub_type.value ? data?.sub_type.value : 'paid_ordinary',
                                }
                              : ''),
                          };

                          if (!data.request_document_id) {
                            formatData.request_document_id = data?.request_document_id;
                            delete formatData.request_document_id;
                          }

                          if (!data.order_document_id) {
                            delete formatData.order_document_id;
                          }

                          await edit(formatData);
                        } catch (error: any) {
                          showModal({
                            type: 'error',
                            content: <>{error.data.errors_common}</>,
                          });
                        }
                      },
                      confirmLabel: t('common.confirm'),
                    });
                  }}
                >
                  <FormGroup
                    controlId="formGroupUsers"
                    label={t('docWizard.step1.docEmployee')}
                    type="select"
                    as="select"
                    name="user_id"
                    isRequired
                    isDisabled={!isManuallyCreatedLeave}
                    selectOptions={dropdowns.user}
                  />
                  <Row className="mt-1">
                    <Col md={{ span: 6, offset: 0 }}>
                      <FormGroup
                        controlId="formGroupType"
                        label={t('leaves.type')}
                        type="select"
                        name="type"
                        selectOptions={dropdowns.types}
                        isRequired
                        isDisabled={!isManuallyCreatedLeave}
                      />
                    </Col>
                    {showSubType && (
                      <Col md={{ span: 6, offset: 0 }}>
                        <FormGroup
                          controlId="formGroupSubType"
                          label={t('leaves.subType')}
                          type="select"
                          name="sub_type"
                          selectOptions={currentSubTypes}
                          isRequired
                          isDisabled={!isManuallyCreatedLeave}
                        />
                      </Col>
                    )}
                  </Row>
                  {isManuallyCreatedLeave && fieldTypeValue === leaveTypes.paid && (
                    <Row>
                      <Col>
                        <FormGroup
                          controlId="formGroupTakePaidLeaveDays"
                          label=""
                          checkType="checkbox"
                          name="take_paid_days"
                          isHorizontal
                          isDisabled={!isManuallyCreatedLeave}
                          options={[
                            {
                              label: t('leaves.takePaidLeaveDays'),
                              name: 'take_paid_days',
                              id: 'take_paid_days',
                              value: 'true',
                            },
                          ]}
                        />
                      </Col>
                    </Row>
                  )}
                  <Row className="mt-1">
                    <Col md={{ span: 6, offset: 0 }}>
                      <FormGroup
                        controlId="formGroupDateFrom"
                        label={t('leaves.dateFrom')}
                        onChange={date => {
                          methods.clearErrors();
                          setStartDate(date);
                          setSelectedYear(date.getFullYear());
                        }}
                        type="datepicker"
                        name="date_from"
                        filterDate={isHoliday}
                        dateFormat="dd.MM.yyyy"
                        isDisabled={!isManuallyCreatedLeave}
                        isRequired
                      />
                    </Col>
                    <Col md={{ span: 6, offset: 0 }}>
                      <FormGroup
                        controlId="formGroupDateTo"
                        label={t('leaves.dateTo')}
                        type="datepicker"
                        minDate={startDate}
                        name="date_to"
                        onChange={date => {
                          methods.clearErrors();
                          setSelectedYear(date.getFullYear());
                        }}
                        filterDate={isHoliday}
                        isDisabled={!isManuallyCreatedLeave}
                        dateFormat="dd.MM.yyyy"
                        isRequired
                      />
                    </Col>
                  </Row>
                  <Row className="mt-1">
                    <Col md={{ span: 6, offset: 0 }}>
                      <FormGroup
                        controlId="formGroupRequestId"
                        label={t('leaves.requestDocumentId')}
                        type="number"
                        name="request_document_id"
                        isDisabled={!isManuallyCreatedLeave}
                      />
                    </Col>
                    <Col md={{ span: 6, offset: 0 }}>
                      <FormGroup
                        controlId="formGroupOrderId"
                        label={t('leaves.orderDocumentId')}
                        type="number"
                        name="order_document_id"
                        isDisabled={!isManuallyCreatedLeave}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup
                        controlId="formGroupComment"
                        label={t('leaves.comment')}
                        type="text"
                        name="comment"
                        maxLength={255}
                      />
                    </Col>
                  </Row>
                  <>
                    <hr />
                    <h4 className="text-center mb-4">{t('docWizard.step3.attachDocuments')}</h4>
                    <FormGroup
                      controlId="formGroupFileUpload"
                      type="file"
                      placeholder={t('fileUploader.placeholder')}
                      name="file"
                      accept={['application/pdf', '.jpg', '.png', '.jpeg']}
                      multiple
                      uploadUri="leaves/upload-attachment"
                      deleteFileUri="leaves/attachments"
                      hint={t('fileUploader.hint', { accept: 'pdf, jpg, png, jpeg' })}
                      isFileUploading={(isUploading: boolean) => {
                        setIsFileUploading(isUploading);
                      }}
                      onFilesUploaded={onFilesUploaded}
                      onFilesDeleted={onFilesDeleted}
                    />
                    {oldUploadedFiles.length > 0 && (
                      <Attachments
                        items={oldUploadedFiles}
                        onDelete={deletedID => {
                          setoldUploadedFiles(
                            oldUploadedFiles.filter((attachment: IАttachment) => attachment.id !== deletedID)
                          );
                        }}
                      />
                    )}
                  </>
                </Form>
              )}
            </UpdateEntity>
          </QueryClientProvider>
        </>
      </Col>
      <Col className="col-lg-5 w-100 mt-4 mt-lg-0">
        {leave.approved_by || leave.rejected_by || leave.signed_by || leave.substracted_days_by_years ? (
          <div className="mt-4">
            <h4 className="text-center">{t('leaves.actions.title')}</h4>
            <table className="table">
              <tbody>
                {leave.created_at && (
                  <tr>
                    <td>
                      <b>{t('leaves.actions.created')}</b> {t('leaves.on')}{' '}
                      {formatDate(new Date(leave.created_at), 'dd.mm.Y hh:ii')}
                      {t('leaves.hoursSmall')}
                    </td>
                  </tr>
                )}
                {leave.approved_by &&
                  leave.approved_by.length > 0 &&
                  formatUserAction(leave.approved_by, t('leaves.actions.approved'))}
                {leave.rejected_by &&
                  leave.rejected_by.length > 0 &&
                  formatUserAction(leave.rejected_by, t('leaves.actions.rejected'))}
                {leave.signed_by &&
                  leave.signed_by.length > 0 &&
                  formatUserAction(leave.signed_by, t('leaves.actions.signed'))}
                {leave.subtracted_days_by_years && formatSubtractedDays(leave.subtracted_days_by_years)}
              </tbody>
            </table>
          </div>
        ) : null}
      </Col>
    </Row>
  );
};

export default ManuallyEdit;
